/**
 * Authentication Guard
 * Redirects to login if not authenticated
 */

import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSession } from "@/state/session";
import { LoadingSpinner } from "./ui/loading-spinner";
import { devLog } from "@/lib/utils";

interface AuthGuardProps {
  children: React.ReactNode;
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isSessionExpired, clearSession, authIsReady } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    devLog('🛡️ AuthGuard MOUNT', { isAuthenticated, authIsReady, path: location.pathname });
    return () => devLog('🛡️ AuthGuard UNMOUNT');
  }, []);

  useEffect(() => {
    // Aguardar authIsReady antes de redirecionar
    if (!authIsReady) {
      devLog('🛡️ AuthGuard: aguardando authIsReady...');
      return;
    }

    devLog('🛡️ AuthGuard STATE', { isAuthenticated, authIsReady, path: location.pathname });
    
    if (isSessionExpired()) {
      devLog('⏱️ Session expired, clearing...');
      clearSession();
    }

    if (!isAuthenticated) {
      devLog('🚫 Not authenticated, redirecting to login');
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [isAuthenticated, authIsReady, navigate, location, isSessionExpired, clearSession]);

  if (!authIsReady || !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" text="Carregando sessão..." />
      </div>
    );
  }

  return <>{children}</>;
}
